import { memo, useState, useCallback, useEffect, useRef } from 'react'
import { Html } from '@react-three/drei'
import { ProxyData } from '../utils'

// Map of which proxies are visible per camera. Use proxy object names
// (e.g. 'cam2_proxy'); only the proxies listed in `visible` are shown.
const proxyVisibility: Record<string, { visible: string[]; showGoBack?: boolean }> = {
  _default: { visible: ['cam1_proxy', 'cam3_proxy', 'cam4_proxy', 'cam5_proxy', 'cam6_proxy', 'cam7_proxy', 'cam8_proxy', 'cam9_proxy', 'cam10_proxy'] },
  cam1: { visible: ['cam2_proxy'], showGoBack: false },
  cam2: { visible: ['cam3_proxy', 'cam4_proxy', 'cam6_proxy', 'cam7_proxy', 'cam8_proxy', 'cam10_proxy', 'cam11_proxy'], showGoBack: false },
  cam3: { visible: ['cam2_proxy', 'cam3_proxy', 'cam4_proxy', 'cam5_proxy', 'cam6_proxy', 'cam7_proxy', 'cam8_proxy', 'cam9_proxy', 'cam10_proxy'] },
  cam4: { visible: [] },
  cam5: { visible: ['cam2_proxy', 'cam3_proxy', 'cam4_proxy', 'cam5_proxy', 'cam6_proxy', 'cam7_proxy', 'cam8_proxy', 'cam9_proxy', 'cam10_proxy'] },
  cam6: { visible: ['cam3_proxy', 'cam4_proxy', 'cam7_proxy', 'cam8_proxy', 'cam10_proxy', 'cam11_proxy'] },
  cam7: { visible: ['cam2_proxy', 'cam3_proxy', 'cam4_proxy', 'cam5_proxy', 'cam6_proxy', 'cam7_proxy', 'cam8_proxy', 'cam9_proxy', 'cam10_proxy'] },
  cam8: { visible: ['cam9_proxy', 'cam6_proxy', 'cam7_proxy'] },
  cam9: { visible: [] },
  cam10: { visible: ['cam3_proxy', 'cam4_proxy', 'cam5_proxy', 'cam6_proxy', 'cam7_proxy', 'cam8_proxy', 'cam10_proxy'] },
  cam11: { visible: ['cam2_proxy'], showGoBack: false },
}

interface NavState { activeCam: number; modalUrl: string | null }

// Camera viewpoint and the interactable modal are both pushed onto the browser
// history, so each history entry fully describes (activeCam, modalUrl) and the
// back button just restores the previous one. Opening a modal pushes an entry
// with the same cam, so Back from it pops to the cam-only entry — closing the
// modal without changing the camera.
export function useCameraSelection() {
  const [activeCam, setActiveCam] = useState(0)
  const [modalUrl, setModalUrl] = useState<string | null>(null)
  // Read inside callbacks without making them depend on (and churn with) the latest cam.
  const activeCamRef = useRef(0)
  activeCamRef.current = activeCam

  useEffect(() => {
    window.history.replaceState({ activeCam: 0, modalUrl: null } satisfies NavState, '')

    const onPopState = (e: PopStateEvent) => {
      const s = (e.state ?? { activeCam: 0, modalUrl: null }) as NavState
      setActiveCam(s.activeCam ?? 0)
      setModalUrl(s.modalUrl ?? null)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const onSelectCam = useCallback((i: number) => {
    window.history.pushState({ activeCam: i, modalUrl: null } satisfies NavState, '')
    setActiveCam(i)
    setModalUrl(null)
  }, [])

  const onOpenModal = useCallback((url: string) => {
    window.history.pushState({ activeCam: activeCamRef.current, modalUrl: url } satisfies NavState, '')
    setModalUrl(url)
  }, [])

  // Closing via the UI just walks history back, so the pushed modal entry is
  // popped and state stays consistent with the back button doing the same.
  const onCloseModal = useCallback(() => { window.history.back() }, [])

  return { activeCam, onSelectCam, modalUrl, onOpenModal, onCloseModal }
}

export function shouldShowGoBack(activeCamIndex: number): boolean {
  const key = `cam${activeCamIndex + 1}`
  const rule = proxyVisibility[key]
  return rule?.showGoBack !== false
}

function isProxyVisible(activeCamIndex: number, proxyName: string): boolean {
  const key = `cam${activeCamIndex + 1}`
  const rule = proxyVisibility[key] ?? proxyVisibility._default
  return rule.visible.includes(proxyName)
}

interface CameraMarkersProps {
  proxies: ProxyData[]
  activeCam: number
  onSelectCam: (i: number) => void
  isVisible: boolean
}

function CameraMarkers({ proxies, activeCam, onSelectCam, isVisible }: CameraMarkersProps) {
  const [hovered, setHovered] = useState<string | null>(null)

  // Clear hover when the camera changes so a marker doesn't stay "lit" after it moves.
  useEffect(() => { setHovered(null) }, [activeCam])

  const onPointerOver = useCallback((name: string) => () => setHovered(name), [])
  const onPointerOut = useCallback(() => setHovered(null), [])
  const onClick = useCallback((proxy: ProxyData) => () => onSelectCam(proxy.targetCam), [onSelectCam])

  if (!isVisible) return null

  return (
    <>
      {proxies.map((proxy) => {
        if (proxy.targetCam === activeCam) return null
        if (!isProxyVisible(activeCam, proxy.name)) return null
        return (
            <group
              key={proxy.name}
              position={proxy.position}
            >
              <Html center style={{ pointerEvents: 'auto' }}>
                <div
                  style={{ position: 'relative', cursor: 'pointer' }}
                  onPointerOver={onPointerOver(proxy.name)}
                  onPointerOut={onPointerOut}
                  onClick={onClick(proxy)}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '2px solid white',
                    background: hovered === proxy.name ? 'white' : 'transparent',
                    margin: '0 auto 8px',
                    transform: hovered === proxy.name ? 'scale(1.5)' : 'scale(1)',
                    opacity: hovered === proxy.name ? 1 : .5,
                    transition: 'transform 0.2s ease, opacity 0.2s ease, background 0.2s ease',
                  }} />
                  <div style={{
                    position: 'relative',
                    color: 'white',
                    fontSize: '1.75rem',
                    fontWeight: 'bold',
                    fontStyle: 'italic',
                    fontFamily: "'Untitled Sans Light', sans-serif",
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                    padding: '12px 21px',
                    background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.5) 0%, transparent 70%)',
                  }}>
                    {proxy.label}
                  </div>
                </div>
              </Html>
            </group>
        )
      })}
    </>
  )
}

export default memo(CameraMarkers)
